import { useEffect, useState } from "react";
import type { Order } from "~/features/orders/types-order";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { formatWindowRange, type WindowMinutes } from "~/shared/utils/delivery-window";
import {
  WindowChoiceFields,
  getInitialWindowChoice,
  getWindowChoiceMinutes,
  type WindowChoice,
} from "~/features/orders/components/WindowChoiceFields";

/** Bulk edit is offered only for two or more selected orders; a single order uses the row edit panel. */
export const BULK_WINDOW_MIN_ORDERS = 2;

type BulkWindowEditPanelProps = Readonly<{
  /** Currently selected orders (kept live, so orders deselected meanwhile drop out) */
  orders: Order[];
  saving: boolean;
  onClose: () => void;
  onApply: (window: WindowMinutes) => void;
}>;

/** Side panel that applies one delivery window (preset or custom) to all selected orders. */
export function BulkWindowEditPanel({ orders, saving, onClose, onApply }: BulkWindowEditPanelProps) {
  const { presets, loading } = useDeliveryWindows();
  const firstWindow = orders[0]?.preferred_window ?? null;
  const [windowChoice, setWindowChoice] = useState<WindowChoice | null>(() =>
    getInitialWindowChoice(firstWindow, presets)
  );
  // Presets may arrive after the panel opens
  useEffect(() => {
    if (windowChoice === null && presets.length > 0) setWindowChoice(getInitialWindowChoice(firstWindow, presets));
  }, [windowChoice, presets, firstWindow]);

  const windowMinutes = getWindowChoiceMinutes(windowChoice);
  const tooFewOrders = orders.length < BULK_WINDOW_MIN_ORDERS;
  const applyDisabled = saving || !windowMinutes || tooFewOrders;

  return (
    <div
      className="modal-panel-enter"
      style={{
        position: "relative",
        background: "white",
        borderRadius: 8,
        border: "1px solid var(--border)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        maxHeight: "calc(100vh - 200px)",
      }}
      aria-label="Edit delivery window for selected orders"
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px 8px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Edit Delivery Window</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Applies to {orders.length} selected order{orders.length === 1 ? "" : "s"}
          </div>
        </div>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            background: "white",
            border: "1px solid var(--border)",
            borderRadius: "50%",
            width: 28,
            height: 28,
            fontSize: 18,
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div style={{ padding: 16, display: "grid", gap: 8, overflow: "auto", flex: 1, minHeight: 0 }}>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 6,
            maxHeight: 180,
            overflowY: "auto",
          }}
        >
          {orders.map((o) => (
            <div
              key={o.id}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                padding: "6px 10px",
                borderBottom: "1px solid var(--border)",
                fontSize: 13,
              }}
            >
              <strong style={{ flexShrink: 0 }}>{o.sm_number}</strong>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {o.patient_name}
              </span>
              <span style={{ flexShrink: 0, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                {formatWindowRange(o.preferred_window) || "--"}
              </span>
            </div>
          ))}
        </div>
        {tooFewOrders && (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Select {BULK_WINDOW_MIN_ORDERS} or more orders to edit their window together.
          </span>
        )}

        <WindowChoiceFields
          value={windowChoice}
          onChange={setWindowChoice}
          presets={presets}
          disabled={saving || loading}
        />
      </div>

      <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          className="primary"
          disabled={applyDisabled}
          onClick={() => {
            if (windowMinutes) onApply(windowMinutes);
          }}
        >
          {saving ? "Applying..." : `Apply to ${orders.length} orders`}
        </button>
      </div>
    </div>
  );
}
